import { useNavigate } from "react-router-dom";
import { BackButtonIcon } from "../../../../icons/academy-icons";
import Results from "./results";
import { useState } from "react";
import CurrencyInput from "./CurrencyInput";
import {
  SubmitHandler,
  useForm,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";
import { toast } from "react-toastify";
import { useUpdateToolMutation } from "../../../../store/api/toolsApi";

enum HRA_STEPS {
  BASIC_SALARY = "basic salary",
  DEARNESS_ALLOWANCE = "dearness allowance",
  HRA_RECEIVED = "HRA received",
  RENT_PAID = "rent paid",
  METRO_CITY = "metro city",
}

export interface HRACalculatorForm {
  basic_salary: number;
  dearness_allowance: number;
  hra_received: number;
  rent_paid: number;
  lives_in_metro: string;
}

const renderStep = (
  step: HRA_STEPS,
  register: UseFormRegister<HRACalculatorForm>,
  watch: UseFormWatch<HRACalculatorForm>,
  errors: any,
) => {
  switch (step) {
    case HRA_STEPS.BASIC_SALARY: {
      const basicSalary = watch("basic_salary");
      return (
        <div className="flex flex-col">
          <div className="font-bold text-3xl text-primary-900 w-full text-center">
            Basic Salary
          </div>
          <div className="text-xl font-normal text-[#040404] w-full text-center">
            {"(Excludes allowances)"}
          </div>
          <img
            className="object-contain my-6"
            width={224}
            height={221}
            src="/assets/money/tax/basic-salary.png"
          />
          <CurrencyInput
            value={basicSalary}
            error={errors.basicSalary?.message}
            register={{
              ...register("basic_salary", {
                valueAsNumber: true,
                required: "Basic Salary is Required.",
                min: {
                  value: 10,
                  message: "Basic Salary must be greater than 10.",
                },
              }),
            }}
          />
        </div>
      );
    }
    case HRA_STEPS.DEARNESS_ALLOWANCE: {
      const dearnessAllowance = watch("dearness_allowance");
      return (
        <div className="flex flex-col items-center">
          <div className="font-bold text-3xl text-primary-900 w-full text-center">
            Dearness Allowance
          </div>
          <div className="text-xl font-normal text-[#040404] w-3/4 text-wrap text-center">
            {
              "This allowance is given to government, public sector employees and pensioners."
            }
          </div>
          <img
            className="object-contain my-6"
            width={224}
            height={221}
            src="/assets/money/tax/dearness-allowance.png"
          />
          <CurrencyInput
            value={dearnessAllowance}
            error={errors.dearness_allowance?.message}
            register={{
              ...register("dearness_allowance", {
                valueAsNumber: true,
                required: "Dearness Allowance is Required.",
                min: {
                  value: 0,
                  message:
                    "Dearness Allowance must be greater than equal to 0.",
                },
              }),
            }}
          />
        </div>
      );
    }
    case HRA_STEPS.HRA_RECEIVED: {
      const hraReceived = watch("hra_received");
      return (
        <div className="flex flex-col">
          <div className="font-bold text-3xl text-primary-900 w-full text-center">
            HRA Received
          </div>
          <div className="text-xl font-normal text-[#040404] text-wrap text-center">
            {"(Check your salary slip)"}
          </div>
          <img
            className="object-contain my-6"
            width={224}
            height={221}
            src="/assets/money/tax/hra-step.png"
          />
          <CurrencyInput
            value={hraReceived}
            error={errors.hraReceived?.message}
            register={{
              ...register("hra_received", {
                valueAsNumber: true,
                min: {
                  value: 0,
                  message: "HRA must be greater than or equal to 0.",
                },
              }),
            }}
          />
        </div>
      );
    }
    case HRA_STEPS.RENT_PAID: {
      const rent = watch("rent_paid");
      return (
        <div className="flex flex-col">
          <div className="font-bold text-3xl text-primary-900 w-full text-center">
            Rent Paid
          </div>
          <img
            className="object-contain my-6"
            width={224}
            height={221}
            src="/assets/money/tax/hra1.webp"
          />
          <CurrencyInput
            value={rent}
            error={errors.rent_paid?.message}
            register={{
              ...register("rent_paid", {
                valueAsNumber: true,
                required: "Rent Paid is Required.",
                min: {
                  value: 10,
                  message: "Rent paid must be greater than equal to 10.",
                },
              }),
            }}
          />
        </div>
      );
    }
    case HRA_STEPS.METRO_CITY:
      return (
        <div className="flex flex-col items-center">
          <div className="font-bold text-3xl text-primary-900 w-full text-center">
            Do you live in Delhi, Mumbai, Kolkata or Chennai?
          </div>
          <div className="text-xl font-normal text-[#040404] w-3/4 text-wrap text-center">
            {
              "People in these cities can claim up to 50% of their basic salary as HRA and others up to 40%."
            }
          </div>
          <img
            className="object-contain my-6"
            width={224}
            height={221}
            src="/assets/money/tax/metro-step.png"
          />
          <div className="flex justify-center w-3/4 gap-10 text-base">
            <div className="bg-primary-300 border border-[#ebebeb] rounded-lg p-4 gap-3 w-1/2 flex justify-start">
              <input
                id="metro-yes"
                type="radio"
                value={"Yes"}
                defaultChecked={true}
                {...register("lives_in_metro")}
              ></input>
              <label htmlFor="metro-yes">Yes</label>
            </div>
            <div className="bg-primary-300 border border-[#ebebeb] rounded-lg gap-3 p-4 w-1/2 flex justify-start">
              <input
                id="metro-no"
                type="radio"
                value={"No"}
                {...register("lives_in_metro")}
              ></input>
              <label htmlFor="metro-no">No</label>
            </div>
          </div>
        </div>
      );
  }
};

const HRACalculator = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<HRA_STEPS>(
    HRA_STEPS.BASIC_SALARY,
  );
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [updateTool] = useUpdateToolMutation();
  const [hraResult, setHraResult] = useState<any>(null);
  const getStep = (step: HRA_STEPS, index: number): HRA_STEPS => {
    const steps = Object.values(HRA_STEPS);
    const currentStepIndex = steps.indexOf(step);
    if (
      currentStepIndex + index < steps.length &&
      currentStepIndex + index >= 0
    ) {
      return steps[currentStepIndex + index];
    }
    return step;
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<HRACalculatorForm>({
    defaultValues: {
      basic_salary: 10000,
      dearness_allowance: 10000,
      hra_received: 10000,
      rent_paid: 10000,
    },
  });

  const handleBack = () => {
    const prevStep = getStep(currentStep, -1);
    if (prevStep === currentStep) {
      navigate("/money/protect/tax");
    } else {
      setCurrentStep(prevStep);
    }
  };

  const handleNext = () => {
    const nextStep = getStep(currentStep, 1);
    setCurrentStep(nextStep);
  };

  const onSubmit: SubmitHandler<HRACalculatorForm> = async (data) => {
    try {
      const response = await updateTool({
        payload: { ...data, commissions: 0 },
        type: "hra_calculator",
        is_save: false,
      });
      setHraResult(response?.data?.data);
      setIsEditing(false);
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    }
  };
  return (
    <div className="flex flex-col gap-8 w-[70%]">
      <div className="font-bold text-3xl flex gap-8 items-center flex-col">
        <div className="flex items-center gap-6 justify-start w-full">
          <div
            className="w-fit h-fit cursor-pointer"
            onClick={() => {
              handleBack();
            }}
          >
            <BackButtonIcon width={20} height={20} />
          </div>
          <p>Calculate HRA</p>
        </div>
        {isEditing ? (
          <div className="flex flex-col items-center bg-white rounded-xl p-6 w-full">
            {/* // progress bar */}
            {
              <div className="flex flex-col items-center justify-center">
                <div className="rounded-lg bg-primary-200 h-2 w-36">
                  <div
                    className="bg-secondary-300 rounded-lg h-2"
                    style={{
                      width:
                        ((Object.values(HRA_STEPS).indexOf(currentStep) + 1) /
                          Object.values(HRA_STEPS).length) *
                          100 +
                        "%",
                    }}
                  />
                </div>
                <div className="bg-[#e7ecfc] rounded-3xl font-normal text-sm mt-4 mb-8 px-2 py-1">
                  Step {Object.values(HRA_STEPS).indexOf(currentStep) + 1} {"/"}
                  {Object.values(HRA_STEPS).length}
                </div>
              </div>
            }
            <form
              id="hra-calculator-form"
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-center"
            >
              {renderStep(currentStep, register, watch, errors)}
              {currentStep !== HRA_STEPS.METRO_CITY ? (
                <button
                  type="button"
                  className="py-3 px-32 font-bold text-md rounded-xl text-white bg-secondary-300 mt-8"
                  onClick={() => {
                    handleNext();
                  }}
                >
                  Next
                </button>
              ) : (
                <input
                  className="py-3 px-32 font-bold text-md rounded-xl text-white bg-secondary-300 mt-8"
                  type="submit"
                  placeholder="Calculate"
                />
              )}
            </form>
          </div>
        ) : (
          <div className="flex flex-col items-center bg-white rounded-xl p-6 w-full">
            <Results setEditing={setIsEditing} {...hraResult} />
          </div>
        )}
      </div>
    </div>
  );
};

export default HRACalculator;
